/**
 * Pin detection and ordering for the conversation list.
 *
 * Pulled out of Chatlistpage so it can be tested on its own: the list has to
 * show pinned chats, pinned-first, on the very first paint - including when the
 * user arrives from another page and the chats are already in redux.
 */

/** The id of the signed-in user, however it happens to be stored. */
export const resolveMyId = (fallback) =>
  String(
    (typeof window !== "undefined" &&
      (window.sessionStorage.getItem("user_id") ||
        window.sessionStorage.getItem("userId"))) ||
      fallback ||
      ""
  );

const pinEntryFor = (chat, myId) =>
  (chat?.pinnedBy || []).find(
    (pin) => String(pin.user?._id || pin.user || "") === myId
  );

/**
 * Is this chat pinned by me?
 *
 * `isPinned` is computed per-request by the server, so it is trusted first.
 * `pinnedBy` is the raw document and covers optimistic local updates, where
 * only that array has been patched.
 */
export const isChatPinnedFor = (chat, myId, allChats) => {
  if (!chat) return false;
  if (chat.isPinned === true) return true;
  if (pinEntryFor(chat, myId)) return true;

  // The row being rendered may be a trimmed copy; fall back to the full list.
  const found = (allChats || []).find(
    (candidate) => String(candidate._id) === String(chat._id)
  );
  if (found && found !== chat) {
    if (found.isPinned === true) return true;
    if (pinEntryFor(found, myId)) return true;
  }

  return Boolean(chat.isPinned);
};

/**
 * Everything compared here becomes unix SECONDS.
 *
 * Message `dateTime` is stored as `moment().unix()` (seconds) while `createdOn`
 * and a server-sent `pinnedAt` are Dates (milliseconds). Comparing the two
 * straight would make any millisecond value beat every second value.
 */
const toSeconds = (value) => {
  if (!value) return 0;
  if (typeof value === "number") {
    return value > 1e12 ? Math.floor(value / 1000) : value;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : Math.floor(parsed / 1000);
};

/** When this chat was pinned by me, in seconds. */
const pinnedAtFor = (chat, myId) => {
  const entry = pinEntryFor(chat, myId);
  return toSeconds(entry?.pinnedAt ?? chat?.pinnedAt ?? 0);
};

/**
 * How recently something happened in this chat, in seconds.
 *
 * A chat with no messages yet falls back to when it was created, so a chat you
 * just started sits at the top instead of below every chat that has ever had a
 * message. Once it has a message, that message's time takes over.
 */
const lastActivityOf = (chat) => {
  const message =
    chat?.latestUnseenMessageDate ||
    chat?.lastMessage?.dateTime ||
    (chat?.messages && chat.messages[chat.messages.length - 1]?.dateTime) ||
    0;

  return toSeconds(message) || toSeconds(chat?.createdOn);
};

/** GROUP / PERSONAL / ALL. */
export const filterChats = (chats, filter) => {
  const list = Array.isArray(chats) ? chats : [];
  if (filter === "GROUP") {
    return list.filter((chat) => (chat.participants || []).length > 2);
  }
  if (filter === "PERSONAL") {
    return list.filter((chat) => (chat.participants || []).length <= 2);
  }
  return list;
};

/**
 * Pinned chats first (most recently pinned first), then by latest activity,
 * then by creation date.
 */
export const sortChatsForList = (chats, myId, allChats) => {
  const list = Array.isArray(chats) ? [...chats] : [];

  return list.sort((a, b) => {
    const aPinned = isChatPinnedFor(a, myId, allChats);
    const bPinned = isChatPinnedFor(b, myId, allChats);

    if (aPinned !== bPinned) return aPinned ? -1 : 1;

    if (aPinned && bPinned) {
      const aPinTime = pinnedAtFor(a, myId);
      const bPinTime = pinnedAtFor(b, myId);
      if (aPinTime && bPinTime && aPinTime !== bPinTime) {
        return bPinTime - aPinTime;
      }
    }

    // `lastActivityOf` already falls back to createdOn, so this one comparison
    // covers "newest message" and "just created" alike.
    const aActivity = lastActivityOf(a);
    const bActivity = lastActivityOf(b);
    if (aActivity !== bActivity) return bActivity - aActivity;

    return 0;
  });
};

/**
 * The list the conversation panel renders.
 *
 * `local` is the page's own copy (patched live by the socket); `fromStore` is
 * what redux holds. The local copy is seeded by an effect, so it is empty on the
 * first paint - falling back to the store means an arriving user sees pinned,
 * sorted chats immediately instead of an unsorted list for a frame.
 */
export const buildChatList = ({ local, fromStore, filter, myId }) => {
  const source = local && local.length ? local : fromStore || [];
  if (!Array.isArray(source)) return [];

  return sortChatsForList(filterChats(source, filter), myId, source);
};
